import React, { FunctionComponent, useState, useRef, useEffect } from 'react';
import {
  AccordionContainer,
  TitleContainer,
  ContentContainer,
  ChildrenContainer,
  LineContainer,
  PrintHideWrapper,
} from './Accordion.style';
import { IAccordionProps, INodeProps } from '../../../types/types';
import { Icon } from '../../atoms/Icons';
import { IconNameType } from '../../../types/iconTypes';
import { Header, Subtitle } from '../../atoms/Typography/Typography';
import { ISubtitleProps, TypographyLevel } from '../../../types/typography';

const Accordion: FunctionComponent<IAccordionProps> = ({
  title = '',
  showLine = true,
  children,
  visibleStandfirst,
  initOpen = false,
  contentType = 'subtitle', // 'header' or 'subtitle'
  semanticLevel = 4,
  displayLevel = 1,
}) => {
  const [openAccordion, setOpenAccordion] = useState<boolean>(initOpen);
  const [textHeight, setTextHeight] = useState<string>('0px');
  const [iconName, setIconName] = useState<IconNameType>(initOpen ? 'Detract' : 'Expand');
  // Contents children visibility is set to prevent it from being keyboard tabbable when the accordion is closed
  const [childrenVisibility, setChildrenVisibility] = useState<boolean>(initOpen);
  const content: INodeProps = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (content?.current && initOpen) {
      setTextHeight(`${content.current.scrollHeight}px`);
    }
  }, [content, initOpen]);

  const toggleAccordion = () => {
    if (!children) return;
    setOpenAccordion(!openAccordion);
    setTextHeight(openAccordion ? '0px' : `${content.current.scrollHeight}px`);
    setIconName(openAccordion ? 'Expand' : 'Detract');
    setChildrenVisibility(!openAccordion);
  };

  const keyDown = (e: { key: string }) => {
    if (e.key === 'Enter' || e.key === 'Space') {
      toggleAccordion();
    }
  };

  const Standfirst = () =>
    visibleStandfirst ? (
      <ContentContainer>
        <ChildrenContainer isVisible={childrenVisibility}>{visibleStandfirst}</ChildrenContainer>
      </ContentContainer>
    ) : null;

  const contentContainerId = `${title}-accordion-content`;

  const AccordionTitle: FunctionComponent<{
    level: TypographyLevel;
    semanticLevel: TypographyLevel;
  }> = ({ level, semanticLevel }) => {
    let subtitleLevel = level;
    if (contentType === 'header') {
      return (
        <Header semanticLevel={semanticLevel} level={level}>
          {title}
        </Header>
      );
    } else if (contentType === 'subtitle') {
      if (contentType === 'subtitle') {
        if (![1, 2].includes(level)) {
          console.warn('Invalid Subtitle level:', level);
          subtitleLevel = level > 2 ? 2 : level;
        }
      }

      return <Subtitle {...({ level: subtitleLevel, semanticLevel } as ISubtitleProps)}>{title}</Subtitle>;
    }

    return null;
  };

  return (
    <AccordionContainer tabIndex={0} onKeyDown={keyDown}>
      <LineContainer />
      <TitleContainer
        onClick={toggleAccordion}
        tabIndex={-1}
        role="button"
        aria-label={title}
        aria-expanded={openAccordion}
        aria-controls={contentContainerId}
      >
        <AccordionTitle level={displayLevel} semanticLevel={semanticLevel}>
          {title}
        </AccordionTitle>
        {children && (
          <PrintHideWrapper>
            <Icon iconName={iconName} />
          </PrintHideWrapper>
        )}
      </TitleContainer>
      {visibleStandfirst && <Standfirst />}
      <ContentContainer
        data-testid="richcontainer"
        ref={content}
        textHeight={textHeight}
        id={contentContainerId}
        aria-live="polite"
      >
        <ChildrenContainer isVisible={childrenVisibility}>{children}</ChildrenContainer>
      </ContentContainer>
      {showLine && <LineContainer />}
    </AccordionContainer>
  );
};

export default Accordion;
